import { useEffect, useState } from "react";
import {
  getB2bikeStatus,
  triggerB2bikeSync,
  type B2bikeStatus,
} from "../../lib/api.js";

export function B2bikeAdminPage(): JSX.Element {
  const [data, setData] = useState<B2bikeStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function load(): Promise<void> {
    try {
      const res = await getB2bikeStatus();
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    void load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, []);

  async function trigger(kind: "reference" | "full" | "light", skipImages = false): Promise<void> {
    setBusy(kind);
    setMsg(null);
    try {
      await triggerB2bikeSync(kind, { skipImages });
      setMsg(`Sync ${kind} spuštěn na pozadí. Sledujte historii níže.`);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-zinc-900">B2Bike — Admin</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Velkoobchodní katalog. Plánovaný denní sync v noci.
        </p>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mb-4">
          {error}
        </div>
      )}

      {data && (
        <>
          {/* Counts */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <StatCard label="Produkty" value={`${data.counts.productsActive} / ${data.counts.productsTotal}`} hint="aktivní / celkem" />
            <StatCard label="Varianty" value={`${data.counts.variantsInStock} / ${data.counts.variantsTotal}`} hint="skladem / celkem" />
            <StatCard label="Obrázky v DB" value={data.counts.imagesCount.toLocaleString("cs-CZ")} />
          </div>

          {/* Triggers */}
          <div className="bg-white rounded-xl ring-1 ring-zinc-200 shadow-sm p-5 mb-6">
            <h2 className="font-semibold text-zinc-900 mb-3">Manuální sync</h2>
            <div className="flex flex-wrap gap-2">
              <TriggerButton label="Reference (kategorie + výrobci)" busy={busy === "reference"} disabled={!!busy} onClick={() => trigger("reference")} />
              <TriggerButton label="Light (ceny + sklady)" busy={busy === "light"} disabled={!!busy} onClick={() => trigger("light")} />
              <TriggerButton label="Full (vše + obrázky)" busy={busy === "full"} disabled={!!busy} onClick={() => trigger("full")} primary />
              <TriggerButton label="Full bez obrázků" busy={busy === "full-skip"} disabled={!!busy} onClick={() => trigger("full", true)} />
            </div>
            {msg && (
              <div className={`mt-3 text-xs ${msg.includes("spuštěn") ? "text-emerald-700" : "text-red-700"}`}>{msg}</div>
            )}
            <div className="mt-3 text-xs text-zinc-400">
              Tip: Full s obrázky první běh trvá ~30 min (200 MB feed + cca 11 tis. obrázků).
              Bez obrázků ~10 min. Light pár sekund.
            </div>
          </div>

          {/* Recent runs */}
          <div className="bg-white rounded-xl ring-1 ring-zinc-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-zinc-100">
              <h2 className="font-semibold text-zinc-900">Historie běhů</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="px-4 py-3 text-left">Typ</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Začátek</th>
                    <th className="px-4 py-3 text-left">Konec</th>
                    <th className="px-4 py-3 text-right">Viděno</th>
                    <th className="px-4 py-3 text-right">+Nové</th>
                    <th className="px-4 py-3 text-right">Δ Změny</th>
                    <th className="px-4 py-3 text-right">Smazáno</th>
                    <th className="px-4 py-3 text-right">Obrázků</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {data.recentRuns.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-4 py-8 text-center text-zinc-400">
                        Žádné běhy.
                      </td>
                    </tr>
                  )}
                  {data.recentRuns.map((r) => (
                    <tr key={r.id} className="hover:bg-zinc-50">
                      <td className="px-4 py-2.5 font-medium">{r.kind}</td>
                      <td className="px-4 py-2.5">
                        <StatusPill status={r.status} />
                      </td>
                      <td className="px-4 py-2.5 text-xs text-zinc-500 whitespace-nowrap">{fmt(r.startedAt)}</td>
                      <td className="px-4 py-2.5 text-xs text-zinc-500 whitespace-nowrap">
                        {r.finishedAt ? fmt(r.finishedAt) : <span className="text-amber-600">probíhá…</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right">{r.productsSeen}</td>
                      <td className="px-4 py-2.5 text-right text-emerald-700">+{r.productsNew}</td>
                      <td className="px-4 py-2.5 text-right text-zinc-500">{r.productsUpdated}</td>
                      <td className="px-4 py-2.5 text-right text-red-500">{r.productsVanished || ""}</td>
                      <td className="px-4 py-2.5 text-right text-zinc-500">{r.imagesDownloaded || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }): JSX.Element {
  return (
    <div className="bg-white rounded-lg ring-1 ring-zinc-200 p-4 shadow-sm">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400 mb-1">{label}</div>
      <div className="text-xl font-semibold text-zinc-900">{value}</div>
      {hint && <div className="text-xs text-zinc-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function TriggerButton({
  label,
  busy,
  disabled,
  onClick,
  primary,
}: {
  label: string;
  busy?: boolean;
  disabled?: boolean;
  onClick: () => void;
  primary?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      className={`px-3.5 py-2 text-sm font-medium rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed ${
        primary
          ? "bg-ktm-orange hover:bg-orange-600 text-white"
          : "bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-50"
      }`}
    >
      {busy ? "Spouštím…" : label}
    </button>
  );
}

function StatusPill({ status }: { status: string }): JSX.Element {
  const cls: Record<string, string> = {
    ok: "bg-emerald-100 text-emerald-700",
    running: "bg-blue-100 text-blue-700",
    partial: "bg-amber-100 text-amber-700",
    failed: "bg-red-100 text-red-700",
  };
  return (
    <span
      className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${cls[status] ?? "bg-zinc-100 text-zinc-700"}`}
    >
      {status}
    </span>
  );
}

function fmt(iso: string): string {
  try {
    return new Date(iso).toLocaleString("cs-CZ", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}
